import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { formatDate } from '../lib/utils';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Plus, 
  MoreVertical, 
  CheckCircle, 
  Trash2, 
  MessageSquare,
  Filter,
  AlertTriangle,
  Clock,
  MessageCircle,
  Wrench,
  Sparkles,
  Shield,
  Users,
  ImagePlus,
  X,
  Image
} from 'lucide-react';
import Pagination from '../components/Pagination';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ITEMS_PER_PAGE = 10;

const categoryConfig = {
  fasilitas: { label: 'Fasilitas', icon: Wrench },
  kebersihan: { label: 'Kebersihan', icon: Sparkles },
  keamanan: { label: 'Keamanan', icon: Shield },
  penghuni_lain: { label: 'Penghuni Lain', icon: Users },
  lainnya: { label: 'Lainnya', icon: MessageSquare },
};

const statusConfig = {
  pending: { label: 'Menunggu', color: 'bg-amber-100 text-amber-700', icon: Clock },
  dalam_proses: { label: 'Dalam Proses', color: 'bg-blue-100 text-blue-700', icon: AlertTriangle },
  selesai: { label: 'Selesai', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
};

const ComplaintForm = ({ onSave, onClose }) => {
  const [formData, setFormData] = useState({
    category: 'fasilitas',
    title: '',
    description: ''
  });
  const [loading, setLoading] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!['image/jpeg', 'image/png', 'image/jpg', 'image/webp'].includes(file.type)) {
        toast.error('Format file tidak didukung. Gunakan JPG, PNG, atau WebP');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Ukuran file maksimal 5MB');
        return;
      }
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.description) {
      toast.error('Mohon isi judul dan deskripsi pengaduan');
      return;
    }

    setLoading(true);
    try {
      let imageUrl = null;
      
      // Upload image if selected
      if (imageFile) {
        setUploadingImage(true);
        const formDataImage = new FormData();
        formDataImage.append('file', imageFile);
        
        const uploadRes = await axios.post(`${API_URL}/api/complaints/upload-image`, formDataImage, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        imageUrl = uploadRes.data.image_url;
        setUploadingImage(false);
      }
      
      // Create complaint with image_url
      await axios.post(`${API_URL}/api/complaints`, {
        ...formData,
        image_url: imageUrl
      });
      toast.success('Pengaduan berhasil dikirim');
      onSave();
    } catch (error) {
      console.error('Error creating complaint:', error);
      toast.error(error.response?.data?.detail || 'Gagal mengirim pengaduan');
    } finally {
      setLoading(false);
      setUploadingImage(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="space-y-2">
        <Label htmlFor="category">Kategori *</Label>
        <Select value={formData.category} onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))}>
          <SelectTrigger className="h-12 bg-muted border-0 rounded-xl" data-testid="complaint-category-select">
            <SelectValue placeholder="Pilih kategori" />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(categoryConfig).map(([key, config]) => (
              <SelectItem key={key} value={key}>{config.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="title">Judul Pengaduan *</Label>
        <Input
          id="title"
          name="title"
          placeholder="Ringkasan masalah"
          value={formData.title}
          onChange={handleChange}
          className="h-12 bg-muted border-0 rounded-xl"
          data-testid="complaint-title-input"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="description">Deskripsi *</Label>
        <Textarea
          id="description"
          name="description"
          placeholder="Jelaskan masalah secara detail..."
          value={formData.description}
          onChange={handleChange}
          className="bg-muted border-0 rounded-xl resize-none"
          rows={4}
          data-testid="complaint-description-input"
        />
      </div>

      {/* Image Upload */}
      <div className="space-y-2">
        <Label>Foto Pendukung (Opsional)</Label>
        {imagePreview ? (
          <div className="relative">
            <img 
              src={imagePreview} 
              alt="Preview" 
              className="w-full h-48 object-cover rounded-xl"
            />
            <Button
              type="button"
              variant="destructive"
              size="icon"
              className="absolute top-2 right-2 h-8 w-8 rounded-full"
              onClick={removeImage}
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        ) : (
          <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-muted-foreground/25 rounded-xl cursor-pointer hover:bg-muted/50 transition-colors">
            <div className="flex flex-col items-center justify-center pt-5 pb-6">
              <ImagePlus className="w-8 h-8 text-muted-foreground mb-2" />
              <p className="text-sm text-muted-foreground">Klik untuk upload gambar</p>
              <p className="text-xs text-muted-foreground mt-1">JPG, PNG, WebP (Maks. 5MB)</p>
            </div>
            <input 
              type="file" 
              className="hidden" 
              accept="image/jpeg,image/png,image/jpg,image/webp"
              onChange={handleImageSelect}
              data-testid="complaint-image-input"
            />
          </label>
        )}
      </div>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose} className="rounded-xl">
          Batal
        </Button>
        <Button type="submit" disabled={loading || uploadingImage} className="rounded-xl" data-testid="complaint-submit-btn">
          {loading || uploadingImage ? (
            <>
              <div className="w-4 h-4 spinner mr-2" />
              {uploadingImage ? 'Mengupload...' : 'Mengirim...'}
            </>
          ) : 'Kirim Pengaduan'}
        </Button>
      </DialogFooter>
    </form>
  );
};

const ResponseForm = ({ complaint, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    status: complaint?.status || 'dalam_proses',
    response: complaint?.response || ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.put(`${API_URL}/api/complaints/${complaint.id}`, formData);
      toast.success('Tanggapan berhasil disimpan');
      onSave();
    } catch (error) {
      toast.error('Gagal menyimpan tanggapan');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="p-4 rounded-xl bg-muted/50 space-y-2">
        <p className="font-medium">{complaint.title}</p>
        <p className="text-sm text-muted-foreground">{complaint.description}</p>
        {complaint.image_url && (
          <div className="mt-3">
            <img 
              src={complaint.image_url} 
              alt="Foto Pengaduan"
              className="w-full max-h-48 object-cover rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
              onClick={() => window.open(complaint.image_url, '_blank')}
            />
          </div>
        )}
        <div className="flex gap-2 text-xs text-muted-foreground pt-2">
          <span>{complaint.tenant_name}</span>
          <span>•</span>
          <span>{complaint.room_name || 'Umum'}</span>
          <span>•</span>
          <span>{formatDate(complaint.created_at)}</span>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="status">Status</Label>
        <Select value={formData.status} onValueChange={(value) => setFormData(prev => ({ ...prev, status: value }))}>
          <SelectTrigger className="h-12 bg-muted border-0 rounded-xl">
            <SelectValue placeholder="Pilih status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="pending">Menunggu</SelectItem>
            <SelectItem value="dalam_proses">Dalam Proses</SelectItem>
            <SelectItem value="selesai">Selesai</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="response">Tanggapan</Label>
        <Textarea
          id="response"
          name="response"
          placeholder="Tulis tanggapan untuk penghuni..."
          value={formData.response}
          onChange={(e) => setFormData(prev => ({ ...prev, response: e.target.value }))}
          className="bg-muted border-0 rounded-xl resize-none"
          rows={3}
        />
      </div>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose} className="rounded-xl">
          Batal
        </Button>
        <Button type="submit" disabled={loading} className="rounded-xl">
          {loading ? <div className="w-5 h-5 spinner" /> : 'Simpan'}
        </Button>
      </DialogFooter>
    </form>
  );
};

const Pengaduan = () => {
  const { canManageKos, isPenghuni } = useAuth();
  const [complaints, setComplaints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [responseDialogOpen, setResponseDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedComplaint, setSelectedComplaint] = useState(null);
  const [page, setPage] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [stats, setStats] = useState({ pending: 0, inProgress: 0, selesai: 0 });

  const fetchComplaints = async () => {
    try {
      const params = new URLSearchParams();
      if (filterStatus) params.append('status', filterStatus);
      if (filterCategory) params.append('category', filterCategory);
      params.append('limit', ITEMS_PER_PAGE.toString());
      params.append('skip', (page * ITEMS_PER_PAGE).toString());
      
      const [response, allComplaintsRes] = await Promise.all([
        axios.get(`${API_URL}/api/complaints?${params.toString()}`),
        // Get all complaints for stats
        axios.get(`${API_URL}/api/complaints?limit=1000`)
      ]);
      
      setComplaints(response.data.data || []);
      setTotalItems(response.data.total || 0);
      
      // Calculate stats from all complaints
      const allComplaints = allComplaintsRes.data.data || [];
      setStats({
        pending: allComplaints.filter(c => c.status === 'pending').length,
        inProgress: allComplaints.filter(c => c.status === 'dalam_proses').length,
        selesai: allComplaints.filter(c => c.status === 'selesai').length
      });
    } catch (error) {
      console.error('Error fetching complaints:', error);
      toast.error('Gagal memuat data pengaduan');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchComplaints();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStatus, filterCategory, page]);

  const handleRespond = (complaint) => {
    setSelectedComplaint(complaint);
    setResponseDialogOpen(true);
  };

  const handleDelete = (complaint) => {
    setSelectedComplaint(complaint);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(`${API_URL}/api/complaints/${selectedComplaint.id}`);
      toast.success('Pengaduan berhasil dihapus');
      fetchComplaints();
    } catch (error) {
      toast.error('Gagal menghapus pengaduan');
    } finally {
      setDeleteDialogOpen(false);
      setSelectedComplaint(null);
    }
  };

  const handleSave = () => {
    setDialogOpen(false);
    setResponseDialogOpen(false);
    setSelectedComplaint(null);
    fetchComplaints();
  };

  return (
    <div className="space-y-8 animate-fade-in" data-testid="pengaduan-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Pengaduan</h1>
          <p className="text-muted-foreground mt-1">
            {canManageKos() ? 'Kelola pengaduan dari penghuni' : 'Sampaikan keluhan atau masalah'}
          </p>
        </div>
        {isPenghuni() && (
          <Button onClick={() => setDialogOpen(true)} className="rounded-xl" data-testid="add-complaint-btn">
            <Plus className="w-5 h-5 mr-2" />
            Buat Pengaduan
          </Button>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-1">
            <Clock className="w-4 h-4 text-amber-600" />
            <span className="text-xs text-muted-foreground">Menunggu</span>
          </div>
          <p className="text-2xl font-bold text-amber-600">{stats.pending}</p>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-1">
            <AlertTriangle className="w-4 h-4 text-blue-600" />
            <span className="text-xs text-muted-foreground">Dalam Proses</span>
          </div>
          <p className="text-2xl font-bold text-blue-600">{stats.inProgress}</p>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-1">
            <CheckCircle className="w-4 h-4 text-emerald-600" />
            <span className="text-xs text-muted-foreground">Selesai</span>
          </div>
          <p className="text-2xl font-bold text-emerald-600">{stats.selesai}</p>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-muted-foreground" />
          <span className="text-sm text-muted-foreground">Filter:</span>
        </div>
        <Select value={filterStatus || "all"} onValueChange={(v) => { setFilterStatus(v === "all" ? "" : v); setPage(0); }}>
          <SelectTrigger className="w-[150px] h-10 bg-muted border-0 rounded-xl">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Status</SelectItem>
            {Object.entries(statusConfig).map(([key, config]) => (
              <SelectItem key={key} value={key}>{config.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterCategory || "all"} onValueChange={(v) => { setFilterCategory(v === "all" ? "" : v); setPage(0); }}>
          <SelectTrigger className="w-[150px] h-10 bg-muted border-0 rounded-xl">
            <SelectValue placeholder="Kategori" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Kategori</SelectItem>
            {Object.entries(categoryConfig).map(([key, config]) => (
              <SelectItem key={key} value={key}>{config.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Complaints List */}
      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => <Skeleton key={i} className="h-32 w-full" />)}
        </div>
      ) : complaints.length === 0 ? (
        <Card className="p-8">
          <div className="empty-state">
            <MessageSquare className="empty-state-icon" />
            <p className="empty-state-title">Belum ada pengaduan</p>
            <p className="empty-state-description">
              {canManageKos() ? 'Pengaduan dari penghuni akan muncul di sini' : 'Sampaikan keluhan jika ada masalah'}
            </p>
          </div>
        </Card>
      ) : (
        <div className="space-y-4">
          {complaints.map((complaint) => {
            const catConfig = categoryConfig[complaint.category] || categoryConfig.lainnya;
            const statConfig = statusConfig[complaint.status] || statusConfig.pending;
            const Icon = catConfig.icon;
            const StatusIcon = statConfig.icon;
            
            return (
              <Card key={complaint.id} className="p-5" data-testid={`complaint-card-${complaint.id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-muted flex items-center justify-center">
                        <Icon className="w-5 h-5" />
                      </div>
                      <div>
                        <h3 className="font-medium">{complaint.title}</h3>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <span>{complaint.tenant_name}</span>
                          <span>•</span>
                          <span>{complaint.room_name || 'Umum'}</span>
                          <span>•</span>
                          <span>{formatDate(complaint.created_at)}</span>
                        </div>
                      </div>
                    </div>
                    
                    <p className="text-sm text-muted-foreground">{complaint.description}</p>
                    
                    {/* Complaint Image */}
                    {complaint.image_url && (
                      <div className="relative">
                        <img 
                          src={complaint.image_url} 
                          alt="Foto Pengaduan"
                          className="w-full max-w-md h-48 object-cover rounded-xl cursor-pointer hover:opacity-90 transition-opacity"
                          onClick={() => window.open(complaint.image_url, '_blank')}
                        />
                        <div className="absolute bottom-2 right-2 bg-black/50 text-white text-xs px-2 py-1 rounded-md flex items-center gap-1">
                          <Image className="w-3 h-3" />
                          Klik untuk memperbesar
                        </div>
                      </div>
                    )}
                    
                    {complaint.response && (
                      <div className="p-3 rounded-lg bg-primary/5 border-l-4 border-primary">
                        <div className="flex items-center gap-2 text-xs text-primary mb-1">
                          <MessageCircle className="w-3 h-3" />
                          <span>Tanggapan Pemilik</span>
                        </div>
                        <p className="text-sm">{complaint.response}</p>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex flex-col items-end gap-2">
                    <Badge className={statConfig.color}>
                      <StatusIcon className="w-3 h-3 mr-1" />
                      {statConfig.label}
                    </Badge>
                    
                    {canManageKos() && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleRespond(complaint)}>
                            <MessageCircle className="w-4 h-4 mr-2" />Tanggapi
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleDelete(complaint)} className="text-destructive">
                            <Trash2 className="w-4 h-4 mr-2" />Hapus
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
          {/* Pagination */}
          {totalItems > ITEMS_PER_PAGE && (
            <Card className="overflow-hidden">
              <Pagination
                currentPage={page}
                totalPages={Math.ceil(totalItems / ITEMS_PER_PAGE)}
                onPageChange={setPage}
                totalItems={totalItems}
                itemsPerPage={ITEMS_PER_PAGE}
              />
            </Card>
          )}
        </div>
      )}

      {/* Create Complaint Dialog (for Penghuni) */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">Buat Pengaduan</DialogTitle>
            <DialogDescription>
              Sampaikan keluhan atau masalah yang Anda alami
            </DialogDescription>
          </DialogHeader>
          <ComplaintForm onSave={handleSave} onClose={() => setDialogOpen(false)} />
        </DialogContent>
      </Dialog>

      {/* Response Dialog (for Pemilik) */}
      <Dialog open={responseDialogOpen} onOpenChange={setResponseDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">Tanggapi Pengaduan</DialogTitle>
            <DialogDescription>
              Berikan tanggapan untuk pengaduan ini
            </DialogDescription>
          </DialogHeader>
          {selectedComplaint && (
            <ResponseForm
              complaint={selectedComplaint}
              onSave={handleSave}
              onClose={() => { setResponseDialogOpen(false); setSelectedComplaint(null); }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus Pengaduan?</AlertDialogTitle>
            <AlertDialogDescription>
              Anda yakin ingin menghapus pengaduan ini?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Batal</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90 rounded-xl">
              Hapus
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Pengaduan;
